<template>
	<div class="swiper-container" :style="backgroundStyle">
		<div v-if="showBullets" class="swiper-bullets">
			<span
				v-for="(_, index) in items"
				:key="index"
				class="swiper-bullet"
				:class="{ active: currentSlideIndex === index }"
			/>
		</div>

		<div class="swiper-title" v-if="showTitle">
			<slot name="title"></slot>
		</div>

		<div class="swiper-container2" ref="swiperContainer" dir="ltr">
			<div class="swiper-wrapper">
				<slot></slot>
			</div>
		</div>

		<button class="swiper-pagination" @click="() => next()" v-if="showSwappers">
			<FontAwesomeIcon :icon="faChevronRight" />
		</button>

		<button class="swiper-pagination left" @click="() => prev()" v-if="showSwappers">
			<FontAwesomeIcon :icon="faChevronLeft" />
		</button>
	</div>
</template>

<script setup lang="ts">
import { AppDirection } from '@/enums/app-direction';
import { computed, onMounted, ref } from 'vue';
import { faChevronRight, faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import { storeToRefs } from 'pinia';
import { useMetaStore } from '@/stores/pinia/useMetaStore';
onMounted(() => setTimeout(initSwiper, 0));

let swiper: any;
const currentSlideIndex = ref(0);
const swiperContainer = ref('swiperContainer');

const emits = defineEmits(['slideChange']);
const props = withDefaults(
	defineProps<{
		titleCount: number;
		showSwappers?: boolean;
		showTitle?: boolean;
		loop?: boolean;
		showBullets?: boolean;
		backgroundImages?: string[];
	}>(),
	{
		showSwappers: true,
		showTitle: true,
		loop: true,
		showBullets: true,
		backgroundImages: () => [],
	}
);

const backgroundStyle = computed(() => {
	const url = props.backgroundImages[currentSlideIndex.value];
	return url ? { '--bg-image': `url(${url})` } : {};
});
const { appDirection } = storeToRefs(useMetaStore());

const items = computed(() => Array.from({ length: props.titleCount }));

const isRTL = computed(() => appDirection.value === AppDirection.RTL);

function next(allowRTL: boolean = false) {
	if (isRTL.value && !allowRTL) {
		prev(true);
		return;
	}

	if (currentSlideIndex.value === props.titleCount - 1) {
		swiper.slideTo(0);
	} else {
		swiper.slideTo(currentSlideIndex.value + 1);
	}
}

function prev(allowRTL: boolean = false) {
	if (isRTL.value && !allowRTL) {
		next(true);
		return;
	}
	if (currentSlideIndex.value === 0) {
		swiper.slideTo(props.titleCount - 1);
	} else {
		swiper.slideTo(currentSlideIndex.value - 1);
	}
}

function changeSlideIndex(index: number, nextIndex: number): void {
	currentSlideIndex.value = nextIndex;

	const position = index + 1;

	if (nextIndex !== index) {
		if (Math.abs(index - nextIndex) === props.titleCount - 1)
			// handle when swiping right from lastIndex or left from 0 index separately
			emits('slideChange', { direction: index === props.titleCount - 1 ? 'right' : 'left', position });
		else emits('slideChange', { direction: index < nextIndex ? 'right' : 'left', position });
	}
}

async function initSwiper(initialSlideIndex: number = 0) {
	const { default: Swiper } = await import('tiny-swiper');
	try {
		if (swiperContainer.value) {
			swiper = new Swiper(swiperContainer.value, {
				initialSlide: initialSlideIndex,
				loop: props.loop,
			});

			swiper.on('before-slide', (current: number, _: any, next: number) => changeSlideIndex(current, next));
		}
	} catch (e) {
		console.log('error', e);
	}
}

defineExpose({
	next,
	prev,
	currentSlideIndex,
});
</script>

<style lang="scss" scoped>
.swiper-container {
	width: 100%;
	height: 100%;
	position: relative;
	overflow: hidden;
	z-index: 1;
	display: flex;
	flex-direction: column;
	background: #0b131e;
	&::before {
		content: '';
		position: absolute;
		inset: 0;
		background-image: var(--bg-image);
		background-size: cover;
		background-position: center top;
		z-index: -1;
	}
	&::after {
		content: '';
		position: absolute;
		inset: 0;
		background: linear-gradient(
			to bottom,
			rgba(11, 19, 30, 0.1) 0%,
			rgba(11, 19, 30, 0.6) 50%,
			rgba(11, 19, 30, 1) 100%
		);
		z-index: -1;
	}
	.swiper-pagination {
		outline: none;
		border: none;
		position: absolute;
		top: 50%;
		inset-inline-end: 16px;
		transform: translateY(-50%);
		z-index: 2;
		font-size: 16px;
		background: #353a42;
		color: #d2d3d5;
		border-radius: 50%;
		width: 32px;
		height: 32px;
		display: flex;
		align-items: center;
		justify-content: center;
		&.left {
			inset-inline-start: 16px;
		}
	}
	.swiper-bullets {
		padding: 24px;
		padding-bottom: 0px;
		display: flex;
		flex-direction: row;
		justify-content: space-between;
		align-items: center;
		gap: 8px;
		direction: ltr;
		z-index: 2;

		.swiper-bullet {
			flex: 1;
			height: 2px;
			background: #6f7d90;
			display: inline-block;
			&.active {
				background: #ffff;
			}
		}
	}
	.swiper-title {
		padding: 0 16px;
		min-height: 48px;
	}
}
.swiper-wrapper {
	display: flex;
	flex-direction: row;
	width: 100%;
	::v-deep {
		.swiper-item {
			position: absolute;
		}
		.swiper-slide {
			min-width: 100%;
		}
	}
}
.swiper-container2 {
	display: flex;
	flex-direction: row;
	overflow: hidden;
	width: 100%;
}
</style>
